import * as React from 'react'
import { Button, styled, emphasize, SxProps } from '@design/pt-ui'
import { elipsedText } from '../../utils/string'

const HEIGHT = 0

const StyledButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== 'isActive' && prop !== 'disableGutters',
})<{ isActive?: boolean; disableGutters?: boolean }>(
  ({ theme, isActive, disableGutters }) => ({
    textTransform: 'capitalize',
    borderRadius: 2,
    color: theme.palette.grey[800],
    fontFamily: theme.typography.fontFamily,
    fontSize: theme.typography.caption.fontSize,
    height: HEIGHT,
    lineHeight: `${HEIGHT}px`,
    '&:active': {
      backgroundColor: emphasize(theme.palette.grey[100], 0.12),
      boxShadow: theme.shadows[0],
    },
    '&:focus, &:hover': {
      backgroundColor: theme.palette.grey[100],
    },
    ...(!disableGutters && {
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    }),
    ...(isActive && {
      fontWeight: theme.typography.fontWeightBold,
    }),
  }),
)

const IconSlot = styled('span')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  '& > *': {
    color:
      theme.palette.mode === 'light'
        ? theme.palette.grey[700]
        : theme.palette.grey[300],
    height: HEIGHT / 1.2,
    marginRight: theme.spacing(0.5),
    width: HEIGHT / 1.2,
  },
}))

export interface BreadcrumbItemProps {
  label: string
  labelLength?: number
  active?: boolean
  tabIndex?: number
  id?: string
  className?: string
  disableGutters?: boolean
  href?: string
  icon?: React.ReactNode
  onClick?: (url: string) => void
  target?: '_blank' | '_parent' | '_self' | '_top' | ''
  sx?: SxProps
}

function BreadcrumbItem(props: BreadcrumbItemProps) {
  const onClick = () => {
    if (props.onClick && !props.active) {
      props.onClick(props.href || '#')
    }
  }

  const {
    className,
    icon: iconProp,
    label,
    active,
    disableGutters,
    tabIndex,
    labelLength,
    sx,
  } = props

  let newTabIndex = tabIndex
  if (!newTabIndex) {
    newTabIndex = props.onClick ? 0 : -1
  }

  const icon = iconProp && React.isValidElement(iconProp) ? iconProp : null

  return (
    <StyledButton
      id={props.id}
      className={className}
      isActive={active}
      disableGutters={disableGutters}
      sx={sx}
      variant="text"
      color="primary"
      tabIndex={tabIndex}
      onClick={onClick}
      title={label}>
      <span style={{ display: 'flex', alignItems: 'center' }}>
        {icon && <IconSlot>{icon}</IconSlot>}
        {labelLength ? elipsedText(labelLength, label) : label}
      </span>
    </StyledButton>
  )
}

export default BreadcrumbItem
